import type {SagaIterator} from 'redux-saga';
import {valid} from 'node-html-parser';

import type {ActionType} from 'constants/flow/flowTypes';
import {CMS_BANNER} from 'constants/actions';

import {putResolve, takeEvery, call, select, getContext} from 'redux-saga/effects';
import {getCmsByAffId, getCmsByApi, getCustomPopup} from 'actions/cms/cms';
import {getCurrentPage} from 'selectors/app/app';
import {getAffId} from 'libs/analytics/affiliate';
import {logWithSentry} from 'libs/sentry/sentry';
import {getSearchParams} from 'selectors/router/router';

/**
 * Returned provided CMS block if it contains valid HTML, null otherwise
 * @param {Action} cmsBlockFetchingAction - request to CMS block action
 * @returns {Promise<Action|null>} - action with valid CMS block or nothing
 */
export const validateBanner = async cmsBlockFetchingAction => {
    const response = await cmsBlockFetchingAction.payload;

    return {
        action: cmsBlockFetchingAction,
        isValid: valid(response?.data?.content),
        identifier: response?.data?.identifier,
    };
};

/**
 * Validate and put CMS block to store, logs error if CMS block is invalid
 * @param {Action} cmsBlockFetchingAction - action to fetch CMS block
 */
export function* handleCmsBannerResponse(cmsBlockFetchingAction) {
    const {action, isValid, identifier} = yield call(validateBanner, cmsBlockFetchingAction);

    if (isValid) {
        yield putResolve(action);
    } else {
        yield call(
            logWithSentry,
            new Error('CMS block has invalid HTML markup'),
            `on: ${action.meta?.bannerName}, identifier: ${identifier}`,
        );
    }
}

/**
 * Get banner by affiliate id
 * @param {string} placeholder - placeholder id
 * @param {string} affId - affiliate id
 * @param {string} bannerId - banner id
 */
export function* getBannerByAffiliate(
    placeholder: string,
    affId: string,
    bannerId: string,
): SagaIterator {
    const currentPage = yield select(getCurrentPage);
    let affiliateAction;
    if (placeholder === '') {
        affiliateAction = yield call(
            getCmsByApi,
            {bannerId, withPopup: true, affId},
            {bannerName: `${currentPage.page}_banner`},
        );
    } else {
        affiliateAction = yield call(
            getCmsByAffId,
            placeholder,
            affId,
            `${currentPage.page}_banner`,
        );
    }
    yield call(handleCmsBannerResponse, affiliateAction);
}

/**
 * Get banner by placeholder or id
 * @param {string} placeholder - placeholder id
 * @param {string} bannerId - banner id
 */
export function* getBannerByPlaceholder(placeholder: string, bannerId: string): SagaIterator {
    try {
        const currentPage = yield select(getCurrentPage);
        const affiliateAction = yield call(
            getCmsByApi,
            {
                bannerId: bannerId || placeholder,
                withPopup: true,
            },
            {bannerName: `${currentPage.page}_banner`},
        );
        yield call(handleCmsBannerResponse, affiliateAction);
    } catch (e) {
        yield call([console, 'log'], e);
    }
}

/**
 * Get affiliate
 * @param {string} placeholder - same as bannerId but uses extended rules from admin panel to get correct banner(preferred to use instead of bannerId)
 * @param {string} bannerId - banner bannerId
 */
export function* getAffiliateBanner(placeholder?: string, bannerId?: string): SagaIterator {
    try {
        const url = yield select(getSearchParams);
        const cookies = yield getContext('cookies');
        const affId = yield call(getAffId, url, cookies);
        if (affId) {
            yield call(getBannerByAffiliate, placeholder, affId, bannerId);
        } else {
            yield call(getBannerByPlaceholder, placeholder, bannerId);
        }
    } catch (error) {
        yield call([console, 'log'], error);
    }
}

/**
 * Fetch cms popup content
 * @param {ActionType<{placeholder: string, bannerId: string}>} action - Action
 */
export function* fetchCmsPopupContent(
    action: ActionType<{placeholder?: string, bannerId?: string}>,
): SagaIterator {
    const {blockName} = action.payload;
    const getPopupAction = yield call(getCustomPopup, blockName, {});
    yield putResolve(getPopupAction);
}

/**
 * Handles START_SAGA_BANNER action and starts getAffiliateBanner task with params passed as payload
 * @param {ActionType<{placeholder: string, bannerId: string}>} action - placeholder mobile_hp_banner,
 */
export function* handleBannerSetup(action: ActionType<{placeholder?: string, bannerId?: string}>) {
    const {placeholder, bannerId} = action.payload;
    yield call(getAffiliateBanner, placeholder, bannerId);
}

/**
 * Begin of saga
 */
export default function* cmsBannerSaga(): SagaIterator {
    yield takeEvery(CMS_BANNER.START_SAGA_BANNER, handleBannerSetup);
    yield takeEvery(CMS_BANNER.GET_POPUP, fetchCmsPopupContent);
}
